import { client } from "@/client";
import SelectInput, {
  SelectInputProps,
} from "@/components/select-input/select-input";
import { cn } from "@/lib/utils";
import {
  getTimeZone,
  getTimeZoneShort,
  TimeSettings,
  useTimeSettings,
} from "@/state/time-settings";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import { SelectInstance } from "react-select";

type TzOption = {
  value: Exclude<TimeSettings["timezone"], undefined>;
  label: string;
  preferAccountTimezone?: true;
  timeZone: string | undefined;
};

export type TzPickerProps = {
  value?: string | undefined;
  onChange?: (tz: string | undefined) => void;
  id?: string;
} & Omit<
  SelectInputProps<string | number, TzOption, false>,
  "onChange" | "value" | "options" | "isMulti" | "isClearable" | "id"
>;

export const TzPicker = React.forwardRef<
  SelectInstance<TzOption, false>,
  TzPickerProps
>(({ value, onChange, className, id, ...props }, ref) => {
  const timeSettings = useTimeSettings();
  const account = useQuery(client.users.me.asQueryOptions());

  const isControlled = value !== undefined && onChange !== undefined;
  const currentValue = isControlled ? value : timeSettings.timezone;

  const tzOptions = React.useMemo(() => {
    const options: TzOption[] = [
      {
        value: "local",
        label: `Local (${getTimeZoneShort("local")})`,
        timeZone: getTimeZone("local"),
      },
      {
        value: "utc",
        label: `UTC (${getTimeZoneShort("utc")})`,
        timeZone: getTimeZone("utc"),
      },
    ];
    if (account.data?.timezone) {
      const tz = account.data.timezone;
      options.unshift({
        value: tz,
        label: `Account Time (${getTimeZoneShort(tz)})`,
        preferAccountTimezone: true,
        timeZone: getTimeZone(tz),
      });
    }
    return options;
  }, [account]);

  const currentOption = React.useMemo(
    () =>
      isControlled
        ? (tzOptions.find((op) => op.timeZone === value) ?? {
            value,
            label: value,
            timeZone: value,
          })
        : tzOptions.find((op) => op.value === timeSettings.timezone),
    [tzOptions, isControlled, currentValue],
  );

  React.useEffect(() => {
    // sync account TZ only in uncontrolled mode
    if (
      !isControlled &&
      timeSettings.preferAccountTimezone &&
      account.data?.timezone
    ) {
      timeSettings.set((s) => ({
        ...s,
        timezone: account.data.timezone,
      }));
    }
  }, [
    isControlled,
    account.data?.timezone,
    timeSettings.preferAccountTimezone,
    timeSettings.set,
  ]);
  return (
    <SelectInput
      ref={ref as any}
      inputId={id}
      {...props}
      className={cn("min-w-36", className)}
      classNames={{
        valueContainer: (_) => cn("min-w-[150px]"),
        control: (_) => cn("h-9"),
      }}
      options={tzOptions}
      value={currentOption}
      onChange={(op) => {
        if (!op) return;
        const tz = op.value;
        if (isControlled) {
          onChange!(op.timeZone);
        } else {
          timeSettings.set((s) => ({
            ...s,
            timezone: tz,
            preferAccountTimezone: op.preferAccountTimezone,
          }));
        }
      }}
      isMulti={false}
      isClearable={false}
    />
  );
});
